/**
 * Overpass API adapter — browser fetch with rate-limit handling.
 *
 * Sends Overpass QL queries to the public Overpass API.
 * Respects HTTP 429 with a single retry after 1 second.
 * All queries already include [timeout:25][maxsize:104857600] guards
 * added by the planner's QL builder.
 *
 * Fair-use reminder: ~10 000 requests/day, <1 GB/day.
 * Tests must use static fixtures — do NOT call this in unit tests.
 */

const OVERPASS_ENDPOINT = 'https://overpass-api.de/api/interpreter';
const USER_AGENT = 'era-open-railway-maps/0.1.0 (https://github.com/era-open-railway-maps)';

export interface OsmElement {
  type: 'node' | 'way' | 'relation';
  id: number;
  lat?: number;
  lon?: number;
  center?: { lat: number; lon: number };
  geometry?: Array<{ lat: number; lon: number }>;
  tags?: Record<string, string>;
  nodes?: number[];
  members?: Array<{ type: string; ref: number; role: string }>;
}

export interface OsmJson {
  version: number;
  generator: string;
  elements: OsmElement[];
}

/**
 * Execute an Overpass QL query and return the parsed OSM JSON response.
 * Retries once on HTTP 429 after a 1-second delay.
 */
export async function fetchOverpass(overpassQL: string): Promise<OsmJson> {
  const body = new URLSearchParams({ data: overpassQL });

  const doRequest = (): Promise<Response> =>
    fetch(OVERPASS_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'User-Agent': USER_AGENT,
        Referer: 'https://github.com/era-open-railway-maps',
      },
      body: body.toString(),
    });

  let response = await doRequest();

  if (response.status === 429) {
    await sleep(1000);
    response = await doRequest();
  }

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new OverpassError(response.status, text.slice(0, 500));
  }

  const json = (await response.json()) as OsmJson;
  return json;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export class OverpassError extends Error {
  constructor(
    public readonly statusCode: number,
    public readonly body: string,
  ) {
    super(`Overpass API returned HTTP ${statusCode}: ${body}`);
    this.name = 'OverpassError';
  }
}
